import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d614ssccm4ll99gupe70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-charcoal text-white">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h3 className="font-serif text-3xl font-bold mb-3">Stay Connected</h3>
          <p className="text-white/70 mb-6">Get health tips, event updates, and inspiring stories delivered to your inbox</p>
          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-readdy-form id="newsletter-form">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="flex-1 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-coral transition-colors text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-coral text-white px-8 py-3 rounded-full font-semibold hover:bg-coral/90 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 text-sm"
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </form>
          {submitMessage && (
            <p className={`mt-3 text-sm ${submitMessage.includes('Thank') ? 'text-coral' : 'text-red-400'}`}>
              {submitMessage}
            </p>
          )}
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Column */}
          <div>
            <img 
              src="https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/70dbd978d7981c696a7c5275cc24c69f.png" 
              alt="1 Heart Health and Wellness Logo" 
              className="h-12 mb-4"
            />
            <p className="text-white/70 text-sm mb-4 leading-relaxed">
              Empowering communities through health education and awareness.
            </p>
            <div className="flex space-x-3">
              <a href="https://facebook.com" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-coral transition-colors cursor-pointer">
                <i className="ri-facebook-fill"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-coral transition-colors cursor-pointer">
                <i className="ri-twitter-x-fill"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-coral transition-colors cursor-pointer">
                <i className="ri-instagram-fill"></i>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-full hover:bg-coral transition-colors cursor-pointer">
                <i className="ri-linkedin-fill"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/programs" className="text-white/70 hover:text-coral transition-colors">Programs</Link></li>
              <li><Link to="/events" className="text-white/70 hover:text-coral transition-colors">Events</Link></li>
              <li><Link to="/get-involved" className="text-white/70 hover:text-coral transition-colors">Volunteer</Link></li>
              <li><Link to="/donate" className="text-white/70 hover:text-coral transition-colors">Donate</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Resources</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/blog" className="text-white/70 hover:text-coral transition-colors">Blog</Link></li>
              <li><Link to="/health-tips" className="text-white/70 hover:text-coral transition-colors">Health Tips</Link></li>
              <li><Link to="/faqs" className="text-white/70 hover:text-coral transition-colors">FAQs</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-coral transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Contact Info</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center space-x-3">
                <i className="ri-mail-line text-coral"></i>
                <a href="mailto:info@1hearthealth.org" className="text-white/70 hover:text-coral transition-colors">info@1hearthealth.org</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0 text-sm text-white/60">
            <div>
              © 2025 1 Heart Health and Wellness. All Rights Reserved.
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="hover:text-coral transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-coral transition-colors">Terms</Link>
              <Link to="/accessibility" className="hover:text-coral transition-colors">Accessibility</Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-coral transition-colors">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}